import React, { useRef, useEffect, useState } from 'react';
import showdown from 'showdown';
import mermaid from 'mermaid';
import { getApiConfig } from '../utils/configLoader';

const ResultDisplay = ({ result, isLoading, uploadProgress = 0 }) => {
  const resultRef = useRef(null);
  const [htmlContent, setHtmlContent] = useState('');
  
  // Initialize mermaid
  useEffect(() => {
    mermaid.initialize({
      startOnLoad: true,
      theme: 'default',
      securityLevel: 'loose'
    });
  }, []);
  
  // Convert markdown to HTML using showdown
  useEffect(() => {
    if (result) {
      const converter = new showdown.Converter({
        tables: true,
        tasklists: true,
        strikethrough: true,
        ghCodeBlocks: true
      });
      const html = converter.makeHtml(result);
      setHtmlContent(html);
    }
  }, [result]);
  
  // Render mermaid diagrams after HTML content is set
  useEffect(() => {
    if (htmlContent && resultRef.current) {
      setTimeout(() => {
        try {
          // Find text containing "graph" or "sequenceDiagram" or "flowchart" outside of code blocks
          const textNodes = Array.from(resultRef.current.childNodes)
            .filter(node => node.nodeType === Node.TEXT_NODE || 
                           (node.nodeType === Node.ELEMENT_NODE && 
                            node.tagName !== 'PRE' && 
                            node.tagName !== 'CODE'));
          
          // Standard code blocks with mermaid
          const mermaidCodeBlocks = resultRef.current.querySelectorAll('pre code.language-mermaid');
          
          // Also try to find any pre/code with mermaid content when class wasn't set correctly
          const potentialMermaidBlocks = Array.from(resultRef.current.querySelectorAll('pre'))
            .filter(pre => {
              const codeEl = pre.querySelector('code');
              if (!codeEl) return false;
              const content = codeEl.textContent.trim();
              return content.startsWith('graph ') || 
                     content.startsWith('sequenceDiagram') || 
                     content.startsWith('flowchart ') || 
                     content.includes('mermaid');
            });
          
          // Process all known mermaid code blocks
          const processBlock = (element, index) => {
            const isPreElement = element.tagName === 'PRE';
            const codeEl = isPreElement ? element.querySelector('code') : null;
            const mermaidCode = codeEl ? codeEl.textContent : element.textContent;
            
            if (!mermaidCode.trim()) return;
            
            // Create a div to hold the rendered diagram
            const diagramDiv = document.createElement('div');
            diagramDiv.className = 'mermaid';
            diagramDiv.id = `mermaid-diagram-${index}`;
            diagramDiv.textContent = mermaidCode;
            
            // Store the original code as an attribute so we can access it later
            diagramDiv.setAttribute('data-original-code', mermaidCode);
            
            // Replace the original element with the diagram div
            if (isPreElement && element.parentElement) {
              element.parentElement.replaceChild(diagramDiv, element);
            } else if (element.parentElement) {
              element.parentElement.replaceChild(diagramDiv, element);
            }
          };
          
          // Process standard mermaid blocks
          mermaidCodeBlocks.forEach(processBlock);
          
          // Process potential mermaid blocks not correctly marked
          potentialMermaidBlocks.forEach((block, index) => {
            // Only process if it wasn't already processed as a standard mermaid block
            if (!block.querySelector('code.language-mermaid')) {
              processBlock(block, mermaidCodeBlocks.length + index);
            }
          });
          
          // Now run mermaid rendering
          mermaid.run();
        } catch (error) {
          console.error('Error rendering mermaid diagrams:', error);
        }
      }, 100); // Small delay to ensure DOM is fully updated
    }
  }, [htmlContent]);
  
  // This function is no longer needed with the new approach

  const copyToClipboard = () => {
    if (!htmlContent) return;
    
    // Clone the current content with rendered diagrams
    const contentToExport = resultRef.current.cloneNode(true);
    document.body.appendChild(contentToExport);
    
    // Select the content
    const range = document.createRange();
    range.selectNode(contentToExport);
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(range);
    
    // Copy the selected content as HTML
    document.execCommand('copy');
    
    // Clean up
    window.getSelection().removeAllRanges();
    document.body.removeChild(contentToExport);
    
    alert('Formatted content copied to clipboard! You can now paste it into Word or other applications.');
  };
  
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  
  const downloadPdf = async () => {
    if (!htmlContent) return;
    
    setIsPdfLoading(true);
    
    try {
      // Wait for any final rendering
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Force re-render if necessary and wait
      if (resultRef.current && resultRef.current.querySelectorAll('.mermaid:not(:empty) svg').length !== resultRef.current.querySelectorAll('.mermaid').length) {
        // console.log("Forcing mermaid.run() for PDF export.");
        try {
          mermaid.run({ nodes: resultRef.current.querySelectorAll('.mermaid') });
          await new Promise(resolve => setTimeout(resolve, 1500)); // Longer wait after explicit run
        } catch(e) {
          console.error("Error during mermaid.run() for PDF:", e);
        }
      }

      const diagramPngs = {}; // Store base64 PNGs keyed by diagram ID
      const textDiagrams = {}; // Still useful for alt text or if PNG fails
      
      // It's crucial to work on the live DOM state that mermaid.js has modified.
      // Clone the resultRef.current to avoid altering the displayed content if we assign IDs.
      const contentToExport = resultRef.current.cloneNode(true);

      const mermaidDivs = contentToExport.querySelectorAll('.mermaid');
      // console.log(`Found ${mermaidDivs.length} .mermaid elements for PDF export.`);

      // Create an array of promises for all conversions
      const conversionPromises = Array.from(mermaidDivs).map(async (div, index) => {
        let diagramId = div.id;
        if (!diagramId) {
          // If a div.mermaid doesn't have an ID, assign one.
          diagramId = `mermaid-export-${index}`;
          div.id = diagramId; // Assign ID to the div in our cloned content
          // console.log(`Assigned dynamic ID ${diagramId} to a .mermaid div for export.`);
        }

        const originalCode = div.getAttribute('data-original-code') || 
                           (div.firstChild && div.firstChild.nodeType === Node.TEXT_NODE ? div.firstChild.textContent.trim() : div.textContent.trim());
        
        if (originalCode) {
          textDiagrams[diagramId] = originalCode;
        } else {
          console.warn(`No original code found for .mermaid div with ID: ${diagramId}`);
        }

        const svgElement = div.querySelector('svg');
        if (svgElement) {
          try {
            const svgString = new XMLSerializer().serializeToString(svgElement);
            const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;

            const image = new Image();
            
            // Create a promise for each image load and canvas conversion
            await new Promise((resolve, reject) => {
              image.onload = () => {
                const canvas = document.createElement('canvas');
                // --- Determine canvas size ---
                // Option 1: Use SVG's explicit width/height if they are pixel values
                let svgWidth = parseFloat(svgElement.getAttribute('width'));
                let svgHeight = parseFloat(svgElement.getAttribute('height'));

                // Option 2: If no explicit width/height, use viewBox (more robust)
                if (isNaN(svgWidth) || isNaN(svgHeight) || svgWidth <= 0 || svgHeight <= 0) {
                  const viewBox = svgElement.getAttribute('viewBox');
                  if (viewBox) {
                    const parts = viewBox.split(' ');
                    svgWidth = parseFloat(parts[2]);
                    svgHeight = parseFloat(parts[3]);
                  }
                }
                
                // Fallback if dimensions still not found
                if (isNaN(svgWidth) || isNaN(svgHeight) || svgWidth <= 0 || svgHeight <= 0) {
                  console.warn(`Could not determine dimensions for SVG ${diagramId}, using fallback.`);
                  svgWidth = 600; // Default/fallback width
                  svgHeight = 400; // Default/fallback height
                }

                // Apply a scale factor for better resolution
                const scaleFactor = 2; // 2x resolution
                canvas.width = svgWidth * scaleFactor;
                canvas.height = svgHeight * scaleFactor;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                  console.error('Could not get 2D context from canvas');
                  reject(new Error('Canvas 2D context unavailable'));
                  return;
                }
                
                // Fill background with white to ensure opaque PNG
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
                
                diagramPngs[diagramId] = canvas.toDataURL('image/png');
                // console.log(`Converted SVG ${diagramId} to PNG (length: ${diagramPngs[diagramId].length})`);
                resolve();
              };
              image.onerror = (err) => {
                console.error(`Error loading SVG into Image object for ${diagramId}:`, err);
                reject(new Error(`Image loading error for SVG ${diagramId}`));
              };
              image.src = svgDataUrl;
            });
          } catch (e) {
            console.error(`Error converting SVG ${diagramId} to PNG:`, e);
            // No PNG for this diagram if conversion fails
          }
        } else {
          console.warn(`No SVG element found in .mermaid div ID: ${diagramId}. Original code snippet: ${(originalCode || '').substring(0,30)}`);
        }
      });

      // Wait for all SVG to PNG conversions to complete
      await Promise.all(conversionPromises);

      // The HTML to send is the innerHTML of our (potentially ID-modified) cloned container.
      const htmlToSend = contentToExport.innerHTML;

      // Debug logging commented out
      // console.log("Sending to backend for PDF generation:", {
      //   htmlLength: htmlToSend.length,
      //   numTextDiagrams: Object.keys(textDiagrams).length,
      //   numDiagramPngs: Object.keys(diagramPngs).length,
      // });
      // if (Object.keys(diagramPngs).length > 0) {
      //   console.log("Diagram PNG IDs:", Object.keys(diagramPngs));
      // }
      
      // Debug logging commented out
      // console.log("HTML CONTENT START -------------------");
      // console.log(htmlToSend);
      // console.log("HTML CONTENT END ---------------------");

      // Make API request to generate PDF
      const authApiClient = require('../auth/authApiClient').authApiClient;
      const apiConfig = getApiConfig();
      const pdfEndpoint = `${apiConfig.chunkedUploadEndpoint}/api/generate-pdf`;
      const response = await authApiClient.post(
        pdfEndpoint,
        {
          html: htmlToSend,
          textDiagrams: textDiagrams,
          diagramPngs: diagramPngs   // Send the base64 PNGs instead of SVGs
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        // Convert base64 PDF to blob
        const pdfData = atob(response.data.pdf);
        const pdfBytes = new Uint8Array(pdfData.length);
        for (let i = 0; i < pdfData.length; i++) {
          pdfBytes[i] = pdfData.charCodeAt(i);
        }
        const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        
        // Create download link and trigger download
        const downloadLink = document.createElement('a');
        downloadLink.href = pdfUrl;
        downloadLink.download = response.data.filename || 'video_query_result.pdf';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        
        // Clean up
        document.body.removeChild(downloadLink);
        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 100);
      } else {
        throw new Error(response.data.message || 'PDF generation failed');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to generate PDF. Please try again later.');
    } finally {
      setIsPdfLoading(false);
    }
  };
  
  if (isLoading) {
    const isUploading = uploadProgress < 100;
    return (
      <div className="processing-spinner">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>
          {isUploading 
            ? `Uploading video: ${uploadProgress}% complete...` 
            : 'Processing video... This may take several minutes depending on the video length.'}
        </p>
        <div className="progress w-75 mb-3">
          <div 
            className="progress-bar progress-bar-striped progress-bar-animated" 
            role="progressbar" 
            style={{ width: isUploading ? `${uploadProgress}%` : '100%' }}
            aria-valuenow={isUploading ? uploadProgress : 100} 
            aria-valuemin="0" 
            aria-valuemax="100"
          ></div>
        </div>
        {!isUploading && (
          <div className="alert alert-info">
            <small>
              <strong>Note:</strong> Your video has been uploaded successfully and is being processed by Gemini AI. 
              This may take several minutes for longer videos (up to 55 minutes supported).
            </small>
          </div>
        )}
      </div>
    );
  }
  
  if (!result) {
    return null;
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Result</h3>
        <div className="d-flex gap-2">
          <button 
            className="btn btn-primary btn-sm" 
            onClick={copyToClipboard}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-clipboard me-1" viewBox="0 0 16 16">
              <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
              <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
            </svg>
            Copy Formatted
          </button>
          <button 
            className="btn btn-danger btn-sm" 
            onClick={downloadPdf}
            disabled={isPdfLoading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-file-pdf me-1" viewBox="0 0 16 16">
              <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z"/>
              <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029zm1.379-1.901c-.166.076-.32.156-.459.238-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361.01.022.02.036.026.044a.266.266 0 0 0 .035-.012c.137-.056.355-.235.635-.572a8.18 8.18 0 0 0 .45-.606zm1.64-1.33a12.71 12.71 0 0 1 1.01-.193 11.744 11.744 0 0 1-.51-.858 20.801 20.801 0 0 1-.5 1.05zm2.446.45c.15.163.296.3.435.41.24.19.407.253.498.256a.107.107 0 0 0 .07-.015.307.307 0 0 0 .094-.125.436.436 0 0 0 .059-.2.095.095 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a3.876 3.876 0 0 0-.612-.053zM8.078 7.8a6.7 6.7 0 0 0 .2-.828c.031-.188.043-.343.038-.465a.613.613 0 0 0-.032-.198.517.517 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822.024.111.054.227.09.346z"/>
            </svg>
            {isPdfLoading ? 'Generating...' : 'Download PDF'}
          </button>
        </div>
      </div>
      
      <div 
        className="result-container" 
        ref={resultRef}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
      
      <div className="copy-instruction mt-3">
        <p className="mb-0"><strong>Tip:</strong> Click "Copy Formatted" to copy the content in a format suitable for pasting into Word or other document editors.</p>
      </div>
    </div>
  );
};

export default ResultDisplay;